/**
 * Adapter: appointment participant PatientResponse → DS PatientHeaderData +
 * PatientHeaderDemographics. Mirrors Flutter, which opens the encounter WITH the
 * appointment's participant patient (shallow identity for the header).
 *
 * Allergies / problems / flags need the full patient + Condition/AllergyIntolerance
 * fetch (FetchPatientDetailEvent) — wired in a follow-up; left empty here.
 */
import type { PatientHeaderData, PatientHeaderDemographics } from '@scripsteam/scrips-signal-ds';
import type { ListAppointmentItem, PatientResponse } from '@/types/appointment-api';
import type { PatientDetail } from '@/types/patient-detail-api';
import type { EncounterCreateContext } from '@/encounter/services/use-encounter';
import { GENDER_VALUE_SET } from './to-gender-id';
import { bookedSpecialtyId } from './booked-specialty';
import { bookedVisitType } from './booked-visit-type';

export interface EncounterPatientNav {
  patient: PatientHeaderData;
  demographics: PatientHeaderDemographics;
  encounterId?: string | null;
  createContext?: EncounterCreateContext;
  /** The appointment provider's MasterManagement specialty GUID — the encounter-template
   *  `specialtyId` axis (PROD-1162). Optional: absent → the axis stays unconstrained. Like
   *  `demographics`, this is tap-time nav context and is not present on a deep link — where the
   *  encounter page reads the same value off its own appointment fetch (PROD-2701). */
  specialtyId?: string;
  /** Appointment slot (ISO) — seeds the rail's Reschedule dialog target (PROD-1246). Both
   *  present together or neither. Tap-time nav context only (absent on reload/deep-link → the
   *  rail simply hides Reschedule, same graceful degradation as `demographics`/`specialtyId`). */
  appointmentStart?: string;
  appointmentEnd?: string;
  /** Appointment status code (`status.code`) — gates the rail's Reschedule action: hidden once
   *  arrived/checked-in/fulfilled (PROD-1256, Flutter `isArrived`). Tap-time nav context. */
  appointmentStatus?: string;
  /** Appointment type code (`appointmentType.code`) — drives the pre-encounter RFV visit-type
   *  chip (first-time vs follow-up), the same field Flutter reads as
   *  `bloc!.appointment?.appointmentType?.code`, and (TS-4, PROD-2820) the encounter-template
   *  `visitType` axis. Tap-time nav context. CORRECTION (TS-4): this note used to say no
   *  single-appointment GET carries it; the Scheduling code does set `AppointmentType` on GetById
   *  (`AppointmentService.cs:3451-3466`), so the template request reads it from there on a deep link
   *  (`useAppointmentStatus` → `lib/booked-visit-type.ts`). UNVERIFIED on a live payload — until a
   *  dev GetById capture confirms it, the pre-encounter chip keeps reading nav only and simply does
   *  not render on reload/deep-link (same graceful degradation as `appointmentStatus`/Reschedule). */
  appointmentTypeCode?: string;
  /** The booked appointment PROFILE's reference id (`appointmentProfile.referenceId`) — sent beside
   *  `visitType` on the encounter-template request (TS-4). Ignored by PracticeManagement in phase 1;
   *  phase 2 resolves it to a practice-defined visit type. Read through a narrow view because the lean
   *  list type does not model `appointmentProfile`, and list rows are not proven to carry it — the page
   *  falls back to its GetById read when this is absent (cross-model review amendment P2). */
  appointmentProfileId?: string;
  /** Free-text appointment comment (`comment`) — the pre-encounter comment banner, Flutter
   *  `bloc!.appointment?.comment`. Tap-time nav context, same degradation as above. */
  appointmentComment?: string;
}

function fullName(p: PatientResponse): string {
  if (p.name) return p.name;
  const joined = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(' ').trim();
  return joined || 'Unknown Patient';
}

function ageFromDob(dob?: string): number | undefined {
  if (!dob) return undefined;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 150 ? age : undefined;
}

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Label → sex: "Male"/"male"/"M" → 'M', "Female"/"F" → 'F', any other non-empty label → 'X'. */
function sexFromLabel(label: string): 'M' | 'F' | 'X' {
  const s = label.toLowerCase();
  if (s.startsWith('m')) return 'M';
  if (s.startsWith('f')) return 'F';
  return 'X';
}

/**
 * Patient gender → sex ('M' | 'F' | 'X'), or undefined when nothing usable is present.
 *
 * PROD-2141: the live appointment list carries `patient.gender` as the ScripsCommon gender
 * value-set GUID (`ddd65f6b…` = Female was observed on dev), NOT a label. The previous
 * label-only mapping read a GUID as "starts with 'd'" → 'X' → `toGenderId('X')` sent the
 * UNKNOWN GUID as the encounter-template gender axis, so every gender-scoped template
 * (General Encounter, pinned Female) came back `[]` for a female patient.
 *
 * Order: known GUID → label (`genderName`, when the raw value is a GUID we cannot place) →
 * raw label. An UNRECOGNISED GUID with no label yields undefined, so the gender axis stays
 * unconstrained (matches every scope) rather than asserting Unknown.
 */
function sexFromGender(g?: string | null, label?: string | null): 'M' | 'F' | 'X' | undefined {
  const raw = g?.trim();
  if (raw) {
    const s = raw.toLowerCase();
    if (s === GENDER_VALUE_SET.male) return 'M';
    if (s === GENDER_VALUE_SET.female) return 'F';
    if (s === GENDER_VALUE_SET.unknown) return 'X';
    if (!GUID_RE.test(s)) return sexFromLabel(raw);
  }
  const l = label?.trim();
  return l ? sexFromLabel(l) : undefined;
}

/** Whole YEARS from a backend age string, for numeric logic (NOT display). Reads the years
 *  component before 'y' ("1y 5m 24d" → 1), a bare number ("46" → 46), or 0 when only months/days
 *  are present ("11m 3d" → 0 — a blind `parseInt` would read 11). Undefined when unparseable. */
function yearsFromAgeString(s: string): number | undefined {
  if (!s) return undefined;
  const y = /(\d+)\s*y/i.exec(s);
  if (y) return parseInt(y[1], 10);
  if (/^\d+$/.test(s)) return parseInt(s, 10);
  if (/\d+\s*[md]/i.test(s)) return 0; // months/days only → under 1 year
  return undefined;
}

const DOB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Birth date for DISPLAY (the DS card renders it verbatim after "DOB "). The wire `birthDate` is a
 *  full ISO datetime (e.g. `1990-05-12T00:00:00`) — show date-only ("12 May 1990"), never the `T`
 *  + time. Defensive: an already date-only or non-ISO string passes through (time component, if
 *  any, stripped). Use the RAW value (not this) for age math. */
function formatDob(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    const [, y, mo, d] = m;
    const mi = Number(mo) - 1;
    const day = Number(d);
    if (mi >= 0 && mi < 12 && day >= 1 && day <= 31) return `${day} ${DOB_MONTHS[mi]} ${y}`;
    return `${y}-${mo}-${d}`;
  }
  // Not an ISO-shaped date (real birthDate values always are) — pass through unchanged rather
  // than risk truncating a non-ISO string on a stray 'T'.
  return s;
}

/** The DS card prepends an "MRN" label; some patient records store the value ALREADY prefixed
 *  ("MRN-00123") → the card would read "MRN MRN-00123". Strip a leading "MRN" token (followed by a
 *  separator or a digit — never a letter, so a value like "MRNX1" is untouched) so the label isn't
 *  doubled. A bare value ("00123") passes through unchanged. */
function cleanMrn(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const stripped = s.replace(/^mrn(?=[\s:#-]|\d)[\s:#-]*/i, '').trim();
  return stripped || s;
}

/**
 * Adapter: the live patient detail (GET /api/Patients/{id}) → the header card's PatientHeaderData +
 * demographics. The tap-time nav only carries a shallow identity (name + age/sex/MRN) and is ABSENT
 * on reload/deep-link — so the card goes bare. This enriches it from the always-available detail
 * fetch: the full name, photo, and (new) PHONE + LANGUAGE, which the DS card renders but nav never
 * supplied (Flutter `patient_info_widget` shows both). Field names verified vs the PM `Patient` type.
 * Every field is conditionally included so a merge `{...nav, ...detail}` never overwrites a present
 * value with `undefined`. Marital status + interpreter-required now render too (DS 1.32.0 added the
 * demographics slots, PROD-1333 #5); proxy rides the contacts (toContacts → DS isProxy). The
 * appt-status badge stays nav-sourced (a separate appointment-status fetch is a follow-up).
 */
export function detailToHeader(d: PatientDetail | null | undefined): {
  patient: Partial<PatientHeaderData>;
  demographics: PatientHeaderDemographics;
  /**
   * The birth date EXACTLY as the record carries it (ISO datetime, e.g. `2025-01-02T00:00:00`), for
   * age math - the growth-chart mapper. `demographics.dob` is the DISPLAY string ("2 Jan 2025") and
   * must never be fed to a date parser.
   */
  dobIso?: string;
} {
  if (!d) return { patient: {}, demographics: {} };
  const name = (d.name || [d.firstName, d.middleName, d.lastName].filter(Boolean).join(' ')).trim();
  const rawDob = d.birthDate || undefined;
  const dob = formatDob(rawDob);
  const mrn = cleanMrn(d.patientMrn);
  const ageStr = d.age != null ? String(d.age).trim() : '';
  const fallbackYears = ageFromDob(rawDob);
  // Numeric years for LOGIC (e.g. the growth-chart age<2 gate / sorting). Prefer the unambiguous
  // DOB-derived value; only when DOB is absent, parse the backend string via yearsFromAgeString —
  // which reads the YEARS component before 'y', NOT a blind leading integer (a `parseInt("11m 3d")`
  // would wrongly yield 11 for an 11-month-old → break the age<2 gate).
  const age = fallbackYears !== undefined ? fallbackYears : yearsFromAgeString(ageStr);
  // Display string shown VERBATIM (Flutter `patient_info_widget` renders patientData.age as-is):
  // the backend age is display-ready ("1y 5m 24d") → keep it; a bare number → append "y"; absent →
  // derive "Ny" from DOB. Infants thus show months/days, not a bare "1y".
  const ageDisplay = ageStr
    ? (/^\d+$/.test(ageStr) ? `${ageStr}y` : ageStr)
    : fallbackYears !== undefined
      ? `${fallbackYears}y`
      : undefined;
  const language = d.languageName?.name?.trim() || undefined;
  const maritalStatus = d.maritalStatusName?.name?.trim() || undefined;
  return {
    patient: {
      ...(name ? { name } : {}),
      ...(d.photoUrl ? { avatar: d.photoUrl } : {}),
    },
    demographics: {
      ...(age !== undefined ? { age } : {}),
      ...(ageDisplay ? { ageDisplay } : {}),
      // Detail carries the display label only (`genderName.name`); no raw GUID is typed on this wire.
      ...(sexFromGender(undefined, d.genderName?.name) ? { sex: sexFromGender(undefined, d.genderName?.name) } : {}),
      ...(maritalStatus ? { maritalStatus } : {}),
      ...(dob ? { dob } : {}),
      ...(mrn ? { mrn } : {}),
      ...(d.phoneNumber ? { phone: d.phoneNumber } : {}),
      ...(language ? { language } : {}),
      // Only when truthy → an absent/false flag never overwrites a nav value, and false ≠ rendered.
      ...(d.interpreterRequired ? { interpreterRequired: true } : {}),
    },
    ...(rawDob ? { dobIso: rawDob } : {}),
  };
}

export function toEncounterPatient(p: PatientResponse): EncounterPatientNav {
  const rawDob = p.birthDate || p.dateOfBirth;
  const dob = formatDob(rawDob);
  const mrn = cleanMrn(p.mrn);
  return {
    patient: {
      name: fullName(p),
      ...(p.photoUrl || p.photo ? { avatar: p.photoUrl || p.photo } : {}),
    },
    demographics: {
      ...(mrn ? { mrn } : {}),
      ...(dob ? { dob } : {}),
      ...(ageFromDob(rawDob) !== undefined ? { age: ageFromDob(rawDob) } : {}),
      ...(sexFromGender(p.gender, p.genderName) ? { sex: sexFromGender(p.gender, p.genderName) } : {}),
    },
  };
}

/** Build the encounter nav payload from a tapped appointment row. */
export function toEncounterNav(item: ListAppointmentItem): EncounterPatientNav {
  const p = item.participant.find((x) => x.patient)?.patient;
  const provider = item.participant.find((x) => x.provider)?.provider;
  const providerId = provider?.id;
  // PROD-1162 · refined by PROD-1496: the encounter-template specialtyId is the VISIT's booked
  // specialty, falling back to the provider participant's. The rule (and why its order is
  // load-bearing) lives in `booked-specialty.ts`, shared with the deep-link path (PROD-2701).
  const specialtyId = bookedSpecialtyId(item, provider);
  const base = p ? toEncounterPatient(p) : { patient: { name: 'Unknown Patient' }, demographics: {} };
  // Reschedule target needs BOTH ends of the slot; forward only when both are present.
  const slot = item.start && item.end ? { appointmentStart: item.start, appointmentEnd: item.end } : {};
  const status = item.status?.code ? { appointmentStatus: item.status.code } : {};
  // Visit type + comment ride the same tap-time channel (see the field docs). Blocks carry
  // `appointmentType: null` (see ListAppointmentItem), so guard the read rather than assume it.
  const visitType = item.appointmentType?.code ? { appointmentTypeCode: item.appointmentType.code } : {};
  // TS-4: the booked profile id, when the row carries one (narrow view — see the field doc).
  const { appointmentProfileId } = bookedVisitType(item);
  const comment = item.comment?.trim() ? { appointmentComment: item.comment.trim() } : {};
  return {
    ...base,
    ...(specialtyId ? { specialtyId } : {}),
    ...slot,
    ...status,
    ...visitType,
    ...(appointmentProfileId ? { appointmentProfileId } : {}),
    ...comment,
    encounterId: item.encounterId ?? null,
    createContext: p
      ? {
          appointmentId: item.id,
          subjectId: p.id,
          serviceProvider: providerId,
          locationName: item.location?.name,
          // Reason for visit from the appointment (Flutter Appointment.reasonCode) —
          // CreateEncounter sends these (PROD-1472); the hook coalesces absent ones to null.
          reasonForVisit: item.reasonCode?.code,
          reasonForVisitName: item.reasonCode?.display,
        }
      : undefined,
  };
}
